/**
 * Data models for the NCCN Guidelines UI (pathway mode).
 *
 * Mirrors the response returned by the ai-service direct-evaluation endpoint
 * `POST /medical/guidelines/fetch-nccn-guidelines` when invoked with
 * `{ direct_eval: true, pathway_mode: true }`, plus the shared evaluation
 * types persisted by the human-review flow.
 * @module nccnGuidelines
 */

/** Reviewer feedback on an AI evaluation verdict. */
export enum FeedbackType {
  Like = "like",
  Dislike = "dislike",
}

/** Human reviewer's overall met/not-met verdict on a guideline. */
export enum HumanVerdict {
  Met = "met",
  NotMet = "not_met",
}

export type EvaluationResult = "True" | "False" | "Undetermined";

export type EvaluationSourceMode = "pathway" | "direct" | "decompose_validate";

export type NCCNLookupType =
  | "regimen"
  | "chemo_regimen_intersection"
  | "chemo_jcode"
  | "supportive_jcode"
  | "jcode";

export type DecompositionStatus = "draft" | "verified";

/**
 * A reviewer comment on an evaluation item.
 * Comments within a tuple are OR-joined; tuples on a node are AND-joined.
 */
export interface UserComment {
  comment: string;
  condition: string;
  updated_at: string;
  updated_by: string;
}

/** How a reviewer captured a supporting chart reference. */
export type ReviewerReferenceType = "text" | "screenshot";

/** Rectangle identifying a selection in PDF page coordinates. */
export interface ReviewerReferenceRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Location of a reviewer reference on a zero-based PDF page. */
export interface ReviewerReferenceRegion {
  page_index: number;
  rect: ReviewerReferenceRect;
}

/**
 * Clinical PDF text or screenshot attached by a reviewer to one criterion.
 *
 * Screenshot content is stored in Firebase Storage; only its path is included
 * in the NCCN evaluation payload.
 */
export interface ReviewerReference {
  /** Client-generated UUID. */
  id: string;
  /** Capture format used by the reviewer. */
  type: ReviewerReferenceType;
  /** Selected PDF text; populated only for text references. */
  text?: string | null;
  /**
   * Firebase Storage path; populated only for screenshot references.
   * Example: `{orderId}/screenshots/{id}.png`.
   */
  image_path?: string | null;
  /** PDF page location of this reference. */
  region: ReviewerReferenceRegion;
  /** Optional reviewer annotation for this reference. */
  comment?: string | null;
}

/** A single reasoning entry with per-point human validation. */
export interface ReasoningPoint {
  /** The reasoning statement. */
  text: string;
  /** Whether the reviewer agrees with this reasoning point (defaults to true). */
  user_validation?: boolean;
}

/** A single supportive/contradictory fact with per-fact human validation. */
export interface EvidenceFact {
  /** The fact statement (may contain `DocChunk#NN` references). */
  text: string;
  /** Whether the reviewer agrees with this fact (defaults to true). */
  user_validation: boolean;
}

/**
 * A single evaluated condition-path. In pathway mode, one item represents one
 * distinct pathway the LLM identifies within a guideline.
 */
export interface EvaluationItem {
  id: string;
  path: string;
  verdict: EvaluationResult;
  /** Evidence supporting this path (with DocChunk# refs), each with per-fact validation. */
  supportive_facts: EvidenceFact[];
  /** Evidence against this path (or missing required info), each with per-fact validation. */
  contradictory_facts: EvidenceFact[];
  /** Narrative reasoning entries, each with per-point human validation. */
  reasoning: ReasoningPoint[];
  /** Medical-record chunks referenced (pathway mode). */
  chunks_used: string[];
  chunks_used_locations?: string[];
  /** Reviewer comments; OR-joined within a tuple, AND-joined across tuples. */
  reviewer_comments: UserComment[];
  ai_answer_feedback?: FeedbackType | null;
  confidence_score?: number | null;
  /** Clinical PDF references attached by the reviewer to this criterion. */
  reviewer_references?: ReviewerReference[];
}

/** Unified evaluation result for a single guideline. */
export interface GuidelineEvaluationData {
  items: EvaluationItem[];
  evaluation_passed: boolean;
  final_result: EvaluationResult;
  source_mode: EvaluationSourceMode | "";
  error?: string | null;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

/** Human reviewer feedback on a guideline's AI evaluation (guideline level). */
export interface HumanReview {
  verdict?: HumanVerdict | null;
  verdict_reasoning?: string | null;
  ai_improvement_notes?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
}

/** Metadata for a guideline record; pathway results live under `evaluation.items`. */
export interface GuidelineEvaluationMetadata {
  evaluation?: GuidelineEvaluationData | null;
  human_review?: HumanReview | null;
}

/** A single node in the criteria tree from guideline decomposition. */
export interface CriteriaNode {
  id: string;
  statement: string;
  operator?: string | null;
  children: CriteriaNode[];
}

/** A single yes/no question decomposed from a guideline statement. */
export interface DecomposedQuestion {
  id: string;
  question: string;
  operator?: string | null;
  children: DecomposedQuestion[];
}

/** A guideline decomposed into a structured criteria tree (decomposition path only). */
export interface DecomposedGuideline {
  original_guideline: string;
  criteria_tree: CriteriaNode[];
  questions: DecomposedQuestion[];
  notes: string[];
  footnotes: string[];
  decomposition_status?: DecompositionStatus | null;
}

/** A single deduplicated NCCN guideline record with its evaluation. */
export interface NCCNGuidelineRecord {
  identifier?: string | null;
  j_code?: string | null;
  drug_name?: string | null;
  drug_brand?: string | null;
  source?: string | null;
  brand_source?: string | null;
  icd_code?: string | null;
  guideline?: string | null;
  guideline_type?: string | null;
  category?: string | null;
  guideline_name?: string | null;
  regimen?: string | null;
  metadata?: GuidelineEvaluationMetadata | null;
  decomposed_json?: string | null;
  evaluation_json?: string | null;
  decomposed?: DecomposedGuideline | null;
  decomposition_status?: DecompositionStatus | null;
}

/** Top-level response model for the NCCN guidelines fetch/evaluation operation. */
export interface FetchNCCNGuidelinesResponse {
  success: boolean;
  order_id?: string | null;
  lookup_type?: NCCNLookupType | null;
  regimen?: string | null;
  icd_code?: string | null;
  j_codes?: string[] | null;
  chemo_j_codes?: string[] | null;
  supportive_j_codes?: string[] | null;
  common_regimens?: string[] | null;
  guidelines?: NCCNGuidelineRecord[] | null;
  unique_guidelines: string[];
  order_evaluation_result?: EvaluationResult | null;
  /** Clinical notes GCS path the evaluation was run against. */
  notes_path?: string | null;
  /**
   * True when the mandatory (AI-Met + AI-Undetermined) review is done.
   */
  review_submitted?: boolean;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  status?: string | null;
  message?: string | null;
  error?: string | null;
}

/** Request body for the NCCN guidelines fetch/evaluation endpoint. */
export interface FetchNCCNGuidelinesRequest {
  order_id: string;
  org_id?: string | null;
  decompose_guideline?: boolean;
  evaluate_guideline?: boolean;
  force_fresh?: boolean;
  direct_eval?: boolean;
  pathway_mode?: boolean;
}

/**
 * Request body for the direct NCCN guidelines fetch endpoint.
 *
 * Bypasses order-document lookup by supplying the codes directly.
 */
export interface FetchNCCNGuidelinesDirectRequest {
  /** J-codes to look up (at least one required). */
  j_codes: string[];
  /** ICD diagnosis code. */
  icd_code: string;
  /** Optional NCCN regimen code (e.g. "COL14") for direct regimen lookup. */
  regimen?: string | null;
  /** Optional regimen description used for fuzzy ranking. */
  regimen_description?: string | null;
  /** When true, decomposes each guideline into structured yes/no questions. */
  decompose_guideline?: boolean;
  /** When true, bypasses the BQ cache and forces fresh LLM decomposition. */
  force_fresh?: boolean;
}
